// src/components/common/ExpiryReminder.js
// Background service: checks for expiring contracts and sends reminders.
// Uses dynamic settings from ExpirySettings list for thresholds and recipients.
// Returns list of contracts expiring within 60 days for the admin popup.

export const runExpiryCheck = async (service, currentUser, isAdmin) => {
  if (!currentUser) return [];
  try {
    // Import services
    const {
      getExpirySettings,
      getNotificationRecipients,
      logNotification,
      buildExpiryEmailHTML,
    } = await import("../../services/expiryService");
    const { buildEmailHTML } = await import("../../services/contractService");

    // Fetch dynamic settings
    const settings = await getExpirySettings(service);

    // If auto-notifications are disabled, only return popup list (don't send emails)
    if (!settings.enableAutoNotifications) {
      console.log("Expiry auto-notifications disabled");
      return await getPopupContracts(service);
    }

    const data = await service.getListItems("Contracts");
    const contracts = (
      data?.value?.map((i) => ({ ...i.fields, id: i.id })) || []
    ).filter((c) => !c.ContractType || !c.ContractType.includes("Addendum"));

    const today = Date.now();
    const expiringSoon = [];
    let notificationsSent = 0;
    let notificationsFailed = 0;

    // Fetch admin emails once
    let adminEmails = [];
    try {
      const adminData = await service.getListItems(
        "AllowedUsers",
        "fields/Role eq 'admin'",
      );
      adminEmails = (adminData?.value || [])
        .map((u) => u.fields.Title)
        .filter(Boolean);
    } catch {}

    for (const contract of contracts) {
      if (
        !contract.ExpiryDate ||
        contract.Status === "Expired" ||
        contract.Status === "Cancelled" ||
        contract.Status === "Close Out"
      )
        continue;

      const daysLeft = Math.ceil(
        (new Date(contract.ExpiryDate) - today) / 86400000,
      );

      // Auto-mark as expired
      if (daysLeft <= 0) {
        await service
          .updateListItem("Contracts", contract.id, { Status: "Expired" })
          .catch(() => {});
        continue;
      }

      // Collect contracts expiring within 60 days for the popup
      if (
        daysLeft <= 60 &&
        contract.IsRenewal == "Yes" &&
        !["Cancelled"].includes(contract.Status)
      ) {
        expiringSoon.push({ ...contract, daysLeft });
      }

      // Check if this matches any notification interval (with 1-day buffer)
      const matchesInterval = settings.notificationIntervals.some(
        (interval) => Math.abs(daysLeft - interval) <= 1,
      );

      if (matchesInterval) {
        try {
          // Get dynamic recipients based on settings
          const recipients = getNotificationRecipients(contract, settings);
          const recipientEmails = [];

          if (recipients.includes("__ADMIN__")) {
            recipientEmails.push(...adminEmails);
          }
          recipientEmails.push(...recipients.filter((r) => r !== "__ADMIN__"));

          const uniqueRecipients = [...new Set(recipientEmails)].filter(
            Boolean,
          );

          if (uniqueRecipients.length > 0) {
            // Build email using new template
            const html = buildExpiryEmailHTML(contract, daysLeft);
            const subject = `[LexCore] Contract Expiry ${daysLeft <= 0 ? "OVERDUE" : "Reminder"}: ${contract.RefNo}`;

            // Send email - COMMENTED OUT FOR NOW
            // await service
            //   .sendEmail(uniqueRecipients, subject, html)
            //   .catch(async (err) => {
            //     // Log failed notification
            //     await logNotification(service, {
            //       contractId: contract.id,
            //       contractRefNo: contract.RefNo,
            //       notificationType: "expiry_reminder",
            //       daysRemaining: daysLeft,
            //       recipientEmails: uniqueRecipients,
            //       subject,
            //       status: "failed",
            //       errorMessage: err.message,
            //     }).catch(() => {});
            //     throw err;
            //   });

            // Log successful notification - COMMENTED OUT (email sending disabled)
            // await logNotification(service, {
            //   contractId: contract.id,
            //   contractRefNo: contract.RefNo,
            //   notificationType: "expiry_reminder",
            //   daysRemaining: daysLeft,
            //   recipientEmails: uniqueRecipients,
            //   subject,
            //   status: "sent",
            // }).catch(() => {});

            // notificationsSent++;
          }
        } catch (err) {
          console.error(
            `Failed to send expiry notification for ${contract.RefNo}:`,
            err,
          );
          notificationsFailed++;
        }
      }
    }

    console.log(
      `Expiry check complete: ${notificationsSent} sent, ${notificationsFailed} failed`,
    );

    // Return sorted by urgency (fewest days first)
    return expiringSoon.sort((a, b) => a.daysLeft - b.daysLeft);
  } catch (e) {
    console.warn("Expiry check failed:", e);
    return [];
  }
};

// Get contracts for admin popup (independent of settings)
async function getPopupContracts(service) {
  try {
    const data = await service.getListItems("Contracts");
    const contracts = (
      data?.value?.map((i) => ({ ...i.fields, id: i.id })) || []
    ).filter((c) => !c.ContractType || !c.ContractType.includes("Addendum"));

    const today = Date.now();
    const expiringSoon = [];

    for (const contract of contracts) {
      if (
        !contract.ExpiryDate ||
        contract.Status === "Expired" ||
        contract.Status === "Cancelled" ||
        contract.Status === "Close Out"
      )
        continue;

      const daysLeft = Math.ceil(
        (new Date(contract.ExpiryDate) - today) / 86400000,
      );

      if (
        daysLeft <= 60 &&
        daysLeft > 0 &&
        contract.IsRenewal === "Yes" &&
        !["Cancelled", "Executed"].includes(contract.Status)
      ) {
        expiringSoon.push({ ...contract, daysLeft });
      }
    }

    return expiringSoon.sort((a, b) => a.daysLeft - b.daysLeft);
  } catch (e) {
    console.warn("Failed to get popup contracts:", e);
    return [];
  }
}
